import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import wheel from '../../assets/wheel.svg'
import { PlaceCondition, PlaceInfo, SchemeData } from '@buseditor/types'
import { BookableSeat, StaticSchemeElement, StaticSchemeTextElement, padding } from '..'
import { IconButton, Stack, Typography, useTheme } from '@mui/material'
import { currencies, isClickedInside } from '@buseditor/utils'
import Konva from 'konva'
import { Vector2d } from 'konva/lib/types'
import { range } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react'
import { Group, Layer, Line, Rect, Stage } from 'react-konva'
import useImage from 'use-image'

export type BookerBaseSeatPriceCondition = Extract<PlaceCondition, { type: 'priceIncrease' | 'discount' }>

export interface BookerBaseSeatPopupContentProps {
    position: Vector2d
    seat: PlaceInfo
    conditions: PlaceCondition[]
    priceConditions: BookerBaseSeatPriceCondition[]
    onClose: () => void
}

export interface BookerBaseProps {
    data: SchemeData | undefined
    sizeMultiplier: number
    size: {
        width: number
        height: number
    }
    allowChoiceFunc: (seat: PlaceInfo) => boolean
    availableFunc: (seat: PlaceInfo) => boolean
    selectedFunc: (seat: PlaceInfo) => boolean
    onSelect: (seat: PlaceInfo, shape: Konva.Node) => void
    onDrag?: (pos: Vector2d) => Vector2d
    topLeftContent?: ReactNode
    showLegend?: boolean
    showControls?: boolean
    scale?: number
    onScaleChange?: (scale: number) => void
    rotation?: BookerBaseRotation
    onRotationChange?: (rotation: -90 | 0 | 90 | 180) => void
    isRotated?: boolean
    onIsRotatedChange?: (isRotated: boolean) => void
    stageStyle?: React.CSSProperties
    id?: string
    renderSeatPopupContent?: (props: BookerBaseSeatPopupContentProps) => ReactNode
}

const inlineSvgToDataUrl = (value: unknown) => {
    if (!value) {
        return ''
    }

    const raw = typeof value === 'string' ? value : (value as { default?: string }).default ?? ''
    const trimmed = raw.trim()

    if (!trimmed) {
        return ''
    }

    if (trimmed.startsWith('data:')) {
        return trimmed
    }

    if (trimmed.startsWith('<svg') || trimmed.startsWith('<?xml')) {
        return `data:image/svg+xml;utf8,${encodeURIComponent(trimmed)}`
    }

    return raw
}

const wheelSource = inlineSvgToDataUrl(wheel)
const scaleSizes = [0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2]
const rotationSteps = [0, 90, 180, -90] as const
export type BookerBaseRotation = -90 | 0 | 90 | 180 | 270

export const normalizeRotation = (rotation?: number): -90 | 0 | 90 | 180 => {
    if (!Number.isFinite(rotation))
        return 0

    const normalized = ((rotation! % 360) + 360) % 360
    if (normalized === 90)
        return 90
    if (normalized === 180)
        return 180
    if (normalized === 270)
        return -90
    return 0
}

const templateSeats = [
    {
        available: true,
        isSelected: false,
        label: 'свободно',
    },
    {
        available: false,
        isSelected: false,
        label: 'забронировано',
    },
    {
        available: true,
        isSelected: true,
        label: 'выбрано',
    },
]

const getAircraftOutlineMetrics = (width: number, height: number) => {
    const nose = Math.max(100, Math.min(width * 0.8, height * 0.6))
    const tail = Math.max(80, Math.min(width * 0.4, height * 0.3))

    return { nose, tail }
}

const rotatePointsClockwise = (points: number[], baseWidth: number) => {
    const rotated: number[] = []
    for (let i = 0; i < points.length; i += 2) {
        const x = points[i]
        const y = points[i + 1]
        rotated.push(y, baseWidth - x)
    }
    return rotated
}

const getAircraftOutlinePoints = (width: number, height: number) => {
    const isVertical = height > width
    const baseWidth = isVertical ? height : width
    const baseHeight = isVertical ? width : height
    const { nose, tail } = getAircraftOutlineMetrics(baseWidth, baseHeight)
    const midY = baseHeight / 2

    const points = [
        tail, 0,
        baseWidth - nose, 0,
        baseWidth, midY,
        baseWidth - nose, baseHeight,
        tail, baseHeight,
        0, midY,
    ]
    return isVertical ? rotatePointsClockwise(points, baseWidth) : points
}

export function BookerBase({
    data,
    sizeMultiplier,
    size,
    allowChoiceFunc,
    availableFunc,
    selectedFunc,
    onSelect,
    onDrag,
    topLeftContent,
    showLegend = true,
    showControls = true,
    scale: controlledScale,
    onScaleChange: onScaleChangeProp,
    rotation: controlledRotation,
    onRotationChange,
    isRotated: controlledIsRotated,
    onIsRotatedChange,
    stageStyle,
    id,
    renderSeatPopupContent,
}: BookerBaseProps) {
    const theme = useTheme()

    const stageRef = useRef<Konva.Stage>(null)
    const resetStagePositionRef = useRef(true)
    const [driverImage] = useImage(wheelSource)
    const [internalScale, setInternalScale] = useState(1)
    const [internalRotation, setInternalRotation] = useState<-90 | 0 | 90 | 180>(0)
    const scale = controlledScale ?? internalScale
    const rotation = useMemo(() => {
        if (controlledRotation !== undefined)
            return normalizeRotation(controlledRotation)

        if (controlledIsRotated !== undefined)
            return controlledIsRotated ? 90 : 0

        return internalRotation
    }, [controlledRotation, controlledIsRotated, internalRotation])
    const clamp = useCallback((value: number, min: number, max: number) => Math.min(max, Math.max(min, value)), [])

    const [busWidth, busHeight, seatWidth, seatHeight, gridSize] = useMemo(() => {
        if (!data)
            return [0, 0, 0, 0, 0];

        const multiplier = !sizeMultiplier ? 1 : sizeMultiplier;
        return [data.bus.width * multiplier, data.bus.height * multiplier, data.bus.seatWidth * multiplier, data.bus.seatHeight * multiplier, data.bus.gridSize * multiplier];
    }, [data, sizeMultiplier]);
    const isAircraft = useMemo(() => !!data?.seats?.some(seat => seat.sectionId || seat.flightClassGroupId), [data])
    const seatBackSide = useMemo<'left' | 'bottom'>(() => (data && data.bus.height > data.bus.width ? 'bottom' : 'left'), [data])
    const stageInset = padding * 2
    const headerOffset = Math.max(padding * 4 + seatHeight, 120)
    const contentBounds = useMemo(() => {
        if (!data)
            return null

        const scale = !sizeMultiplier ? 1 : sizeMultiplier
        const layerWidth = busWidth + gridSize * 2
        const nominalLeft = 0
        const nominalTop = 0
        const nominalRight = layerWidth * data.bus.layers
        const nominalBottom = busHeight

        let minX = nominalLeft
        let maxX = nominalRight
        let minY = nominalTop
        let maxY = nominalBottom

        if (isAircraft) {
            for (let level = 0; level < data.bus.layers; level++) {
                const levelOffsetX = layerWidth * level
                const points = getAircraftOutlinePoints(busWidth, busHeight)
                for (let i = 0; i < points.length; i += 2) {
                    const x = levelOffsetX + points[i]
                    const y = points[i + 1]
                    minX = Math.min(minX, x)
                    maxX = Math.max(maxX, x)
                    minY = Math.min(minY, y)
                    maxY = Math.max(maxY, y)
                }
            }
        }

        for (const seat of data.seats) {
            const x = seat.x * scale
            const y = seat.y * scale
            const width = (seat.width || data.bus.seatWidth) * scale
            const height = (seat.height || data.bus.seatHeight) * scale
            minX = Math.min(minX, x)
            maxX = Math.max(maxX, x + width)
            minY = Math.min(minY, y)
            maxY = Math.max(maxY, y + height)
        }

        for (const element of data.elements) {
            const x = element.x * scale
            const y = element.y * scale
            const width = element.width * scale
            const height = element.height * scale
            minX = Math.min(minX, x)
            maxX = Math.max(maxX, x + width)
            minY = Math.min(minY, y)
            maxY = Math.max(maxY, y + height)
        }

        const strokePadding = 4
        return {
            minX: minX - strokePadding,
            maxX: maxX + strokePadding,
            minY: minY - strokePadding,
            maxY: maxY + strokePadding,
        }
    }, [data, sizeMultiplier, busWidth, busHeight, gridSize, isAircraft])
    const viewContentBounds = useMemo(() => {
        if (!contentBounds)
            return null

        if (rotation === 0)
            return contentBounds

        const width = contentBounds.maxX - contentBounds.minX
        const height = contentBounds.maxY - contentBounds.minY
        if (rotation === 90) {
            return {
                minX: 0,
                maxX: height,
                minY: contentBounds.minX,
                maxY: contentBounds.maxX,
            }
        }

        if (rotation === 180) {
            return {
                minX: 0,
                maxX: width,
                minY: 0,
                maxY: height,
            }
        }

        // -90 / 270
        return {
            minX: 0,
            maxX: height,
            minY: 0,
            maxY: width,
        }
    }, [contentBounds, rotation])

    const getStageBounds = useCallback((currentScale: number) => {
        if (!data || !viewContentBounds)
            return null

        const contentWidth = (viewContentBounds.maxX - viewContentBounds.minX) * currentScale
        const contentHeight = (viewContentBounds.maxY - viewContentBounds.minY) * currentScale
        const totalHeight = contentHeight + headerOffset
        const scaledMinX = viewContentBounds.minX * currentScale
        const scaledMaxX = viewContentBounds.maxX * currentScale
        const scaledMinY = (viewContentBounds.minY + headerOffset) * currentScale
        const scaledMaxY = (viewContentBounds.maxY + headerOffset) * currentScale

        const baseLeft = stageInset - scaledMinX
        const baseRight = size.width - stageInset - scaledMaxX
        const baseTop = headerOffset - scaledMinY
        const baseBottom = size.height - scaledMaxY

        const baseMinX = Math.min(baseLeft, baseRight)
        const baseMaxX = Math.max(baseLeft, baseRight)
        const baseMinY = Math.min(baseTop, baseBottom)
        const baseMaxY = Math.max(baseTop, baseBottom)

        const curvePanMargin = isAircraft
            ? Math.max(padding * 2, Math.round(Math.min(busWidth, busHeight) * 0.08))
            : padding * 2
        const panMargin = curvePanMargin
        const minX = baseMinX - panMargin
        const maxX = baseMaxX + panMargin
        const allowVerticalPan = totalHeight > size.height
        const minY = allowVerticalPan ? baseMinY - panMargin : baseMinY
        const maxY = allowVerticalPan ? baseMaxY + panMargin : baseMaxY

        const centeredX = (size.width - ((viewContentBounds.minX + viewContentBounds.maxX) * currentScale)) / 2
        const startX = clamp(centeredX, minX, maxX)
        const startY = clamp(baseTop, minY, maxY)

        return { minX, maxX, minY, maxY, startX, startY, contentWidth, contentHeight }
    }, [data, viewContentBounds, isAircraft, busWidth, busHeight, headerOffset, stageInset, size.width, size.height, clamp])
    const stageBounds = useMemo(() => getStageBounds(scale), [getStageBounds, scale])

    useEffect(() => {
        resetStagePositionRef.current = true
    }, [data, rotation])

    const setScale = useCallback((value: number | ((prev: number) => number)) => {
        const prevScale = scale
        const nextScale = typeof value === 'function' ? value(prevScale) : value

        if (nextScale === prevScale) {
            return
        }

        const stage = stageRef.current
        const nextBounds = getStageBounds(nextScale)

        if (stage && nextBounds) {
            const oldScale = stage.scaleX() || prevScale || 1
            const pointer = stage.getPointerPosition() || { x: size.width / 2, y: size.height / 2 }
            const mousePointTo = {
                x: (pointer.x - stage.x()) / oldScale,
                y: (pointer.y - stage.y()) / oldScale,
            }

            const newPos = {
                x: pointer.x - mousePointTo.x * nextScale,
                y: pointer.y - mousePointTo.y * nextScale,
            }

            stage.position({
                x: clamp(newPos.x, nextBounds.minX, nextBounds.maxX),
                y: clamp(newPos.y, nextBounds.minY, nextBounds.maxY),
            })
            stage.batchDraw()
        }

        if (controlledScale === undefined) {
            setInternalScale(nextScale)
        }
        onScaleChangeProp?.(nextScale)
    }, [scale, getStageBounds, size.width, size.height, clamp, controlledScale, onScaleChangeProp])

    const setRotation = useCallback((value: number | ((prev: -90 | 0 | 90 | 180) => number)) => {
        const prevRotation = rotation
        const nextRotation = normalizeRotation(typeof value === 'function' ? value(prevRotation) : value)

        if (nextRotation === prevRotation) {
            return
        }

        if (controlledRotation === undefined && controlledIsRotated === undefined) {
            setInternalRotation(nextRotation)
        }
        onRotationChange?.(nextRotation)
        onIsRotatedChange?.(nextRotation !== 0)
    }, [rotation, controlledRotation, controlledIsRotated, onRotationChange, onIsRotatedChange])

    const nearestScaleIndex = useMemo(() => {
        if (!Number.isFinite(scale)) {
            return 0
        }

        const exact = scaleSizes.indexOf(scale)
        if (exact >= 0) {
            return exact
        }

        let nearest = 0
        let minDiff = Number.POSITIVE_INFINITY
        for (let i = 0; i < scaleSizes.length; i++) {
            const diff = Math.abs(scaleSizes[i] - scale)
            if (diff < minDiff) {
                minDiff = diff
                nearest = i
            }
        }

        return nearest
    }, [scale])
    const canZoomOut = scale > scaleSizes[0]
    const canZoomIn = scale < scaleSizes[scaleSizes.length - 1]
    const zoomOut = () => setScale(scaleSizes[Math.max(0, nearestScaleIndex - 1)])
    const zoomIn = () => setScale(scaleSizes[Math.min(scaleSizes.length - 1, nearestScaleIndex + 1)])
    const rotationStepIndex = rotationSteps.indexOf(rotation)
    const rotateNext = () => setRotation(rotationSteps[(rotationStepIndex + 1) % rotationSteps.length])
    const rotatedGroupTransform = useMemo(() => {
        if (!contentBounds || rotation === 0)
            return { x: 0, y: 0, rotation: 0 }

        if (rotation === 90) {
            return {
                x: contentBounds.maxY,
                y: 0,
                rotation: 90,
            }
        }

        if (rotation === 180) {
            return {
                x: contentBounds.maxX,
                y: contentBounds.maxY,
                rotation: 180,
            }
        }

        // -90 / 270
        return {
            x: -contentBounds.minY,
            y: contentBounds.maxX,
            rotation: -90,
        }
    }, [rotation, contentBounds])
    const displayedRotationText = rotation === -90 ? '-90°' : `${rotation}°`
    const nextRotationTitle = rotationSteps[(rotationStepIndex + 1) % rotationSteps.length]
    const nextRotationTitleText = nextRotationTitle === -90 ? '-90° (270°)' : `${nextRotationTitle}°`

    const [popup, setPopup] = useState<{ position: Vector2d, seat: PlaceInfo, conditions: PlaceCondition[] }>()
    const handleHover = useCallback((seat: PlaceInfo, position?: Vector2d) => {
        if (position && seat && data) {
            document.body.style.cursor = allowChoiceFunc(seat) ? 'pointer' : 'not-allowed';
            setPopup({
                seat: seat,
                position: position,
                conditions: data.conditions || [],
            })
        }
        else {
            setPopup(undefined)
            document.body.style.cursor = 'default'
        }
    }, [data, allowChoiceFunc/*, tour*/])

    const legendSeatSize = Math.max(28, Math.round(Math.min(seatWidth, seatHeight)));
    const legendSeatCorner = Math.min(10, Math.round(legendSeatSize / 4));
    const legendSeatBar = Math.min(8, Math.round(legendSeatSize / 3));

    useEffect(() => {
        if (!stageRef.current || !stageBounds)
            return

        const stage = stageRef.current
        if (resetStagePositionRef.current) {
            stage.position({ x: stageBounds.startX, y: stageBounds.startY })
            resetStagePositionRef.current = false
        }
        else {
            const current = stage.position()
            stage.position({
                x: clamp(current.x, stageBounds.minX, stageBounds.maxX),
                y: clamp(current.y, stageBounds.minY, stageBounds.maxY),
            })
        }

        stage.batchDraw()
    }, [stageBounds, clamp])

    return (
        <>
            {data ? (
                <div style={{ position: 'relative', width: size.width, height: size.height }}>
                    {showControls ? <div style={{ position: 'absolute', left: padding * 2, bottom: padding * 2, zIndex: 2 }}>
                        <Stack
                            direction={size.width < 700 ? 'column' : 'row'}
                            spacing={1}
                            alignItems="center"
                            sx={{
                                backgroundColor: theme.palette.background.paper,
                                borderRadius: 1,
                                p: 0.5,
                                boxShadow: theme.shadows[2],
                            }}>
                            <IconButton disabled={!canZoomOut} onClick={zoomOut}>
                                <ZoomOutIcon />
                            </IconButton>
                            <Typography variant={size.width < 700 ? 'caption' : 'subtitle2'}>{Math.round(scale * 100)}%</Typography>
                            <IconButton disabled={!canZoomIn} onClick={zoomIn}>
                                <ZoomInIcon />
                            </IconButton>
                            <Typography variant={size.width < 700 ? 'caption' : 'subtitle2'}>{displayedRotationText}</Typography>
                            <IconButton
                                title={`Повернуть к ${nextRotationTitleText}`}
                                onClick={rotateNext}>
                                <RotateRightIcon color={rotation === 0 ? 'inherit' : 'primary'} />
                            </IconButton>
                        </Stack>
                    </div> : null}
                    {topLeftContent ? (
                        <div style={{ position: 'absolute', top: padding * 2, left: padding * 2, zIndex: 2 }}>
                            {topLeftContent}
                        </div>
                    ) : null}
                    {showLegend ? <div
                        style={{
                            position: 'absolute',
                            top: padding * 2,
                            right: padding * 2,
                            zIndex: 2,
                            display: 'flex',
                            gap: padding * 2,
                            alignItems: 'center',
                            pointerEvents: 'none',
                        }}>
                        {templateSeats.map(t => {
                            const fill = t.isSelected && t.available
                                ? theme.palette.success.main
                                : !t.available
                                    ? theme.palette.grey['600']
                                    : 'transparent';

                            return (
                                <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: padding / 2 }}>
                                    <div
                                        style={{
                                            width: legendSeatSize,
                                            height: legendSeatSize,
                                            borderRadius: legendSeatCorner,
                                            border: `1px solid ${theme.palette.text.primary}`,
                                            backgroundColor: fill,
                                            position: 'relative',
                                            boxSizing: 'border-box',
                                        }}>
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: seatBackSide === 'bottom' ? 5 : 2,
                                                top: seatBackSide === 'bottom'
                                                    ? Math.max(0, legendSeatSize - legendSeatBar - 5)
                                                    : 5,
                                                width: seatBackSide === 'bottom' ? Math.max(2, legendSeatSize - 10) : legendSeatBar,
                                                height: seatBackSide === 'bottom' ? legendSeatBar : Math.max(2, legendSeatSize - 10),
                                                borderRadius: legendSeatCorner,
                                                backgroundColor: theme.palette.grey['700'],
                                            }}
                                        />
                                    </div>
                                    <Typography variant="body2" style={{ color: theme.palette.text.primary }}>
                                        — {t.label}
                                    </Typography>
                                </div>
                            )
                        })}
                    </div> : null}
                    <Stage
                        style={stageStyle}
                        id={id}
                        ref={stageRef}
                        width={size.width}
                        height={size.height}
                        scale={{ x: scale, y: scale }}
                        draggable
                        onWheel={e => {
                            let direction = e.evt.deltaY > 0 ? -1 : 1
                            if (e.evt.ctrlKey)
                                direction = -direction

                            if ((direction > 0 && !canZoomIn) || (direction < 0 && !canZoomOut))
                                return

                            e.evt.preventDefault()
                            setScale(scaleSizes[direction > 0
                                ? Math.min(scaleSizes.length - 1, nearestScaleIndex + 1)
                                : Math.max(0, nearestScaleIndex - 1)])
                        }}
                        onMouseEnter={() => {
                            //document.body.style.cursor = 'grab';
                        }}
                        onMouseDown={() => {
                            //document.body.style.cursor = 'grabbing';
                        }}
                        onMouseUp={() => {
                            //document.body.style.cursor = 'grab';
                        }}
                        onMouseLeave={() => {
                            //document.body.style.cursor = 'default';
                        }}
                        dragBoundFunc={pos => {
                            if (onDrag)
                                return onDrag(pos);

                            if (!stageBounds)
                                return pos

                            const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

                            return ({
                                x: clamp(pos.x, stageBounds.minX, stageBounds.maxX),
                                y: clamp(pos.y, stageBounds.minY, stageBounds.maxY),
                            });
                        }}>
                        <Layer />
                        <Layer y={headerOffset}>
                            <Group
                                x={rotatedGroupTransform.x}
                                y={rotatedGroupTransform.y}
                                rotation={rotatedGroupTransform.rotation}>
                            {range(0, data.bus.layers).map(level => {
                                const levelOffsetX = (busWidth + (gridSize * 2)) * level
                                return isAircraft ? (
                                    <Line
                                        key={level}
                                        x={levelOffsetX}
                                        y={0}
                                        points={getAircraftOutlinePoints(busWidth, busHeight)}
                                        closed
                                        tension={0.35}
                                        strokeWidth={2}
                                        stroke={theme.palette.text.primary}
                                    />
                                ) : (
                                    <Rect
                                        key={level}
                                        x={levelOffsetX}
                                        y={0}
                                        width={busWidth}
                                        height={busHeight}
                                        cornerRadius={20}
                                        strokeWidth={2}
                                        stroke={theme.palette.text.primary}
                                    />
                                )
                            })}
                            {data.seats.map((seat, i) => (
                                <BookableSeat
                                    key={i}
                                    {...seat}
                                    width={(seat.width || data.bus.seatWidth) * (!sizeMultiplier ? 1 : sizeMultiplier)}
                                    height={(seat.height || data.bus.seatHeight) * (!sizeMultiplier ? 1 : sizeMultiplier)}
                                    x={seat.x * (!sizeMultiplier ? 1 : sizeMultiplier)}
                                    y={seat.y * (!sizeMultiplier ? 1 : sizeMultiplier)}
                                    realX={(seat.realX ?? seat.x) * (!sizeMultiplier ? 1 : sizeMultiplier)}
                                    realY={(seat.realY ?? seat.y) * (!sizeMultiplier ? 1 : sizeMultiplier)}
                                    driverImage={driverImage!}
                                    available={availableFunc(seat)}
                                    isSelected={selectedFunc(seat)}
                                    seatBackSide={seatBackSide}
                                    handleHover={handleHover}
                                    onSelect={(_, shape) => onSelect(seat, shape)}
                                />
                            ))}
                            {data.elements.map((element, i) => element.type == 'block' ?
                                <StaticSchemeElement
                                    key={i}
                                    {...element}
                                    width={element.width * (!sizeMultiplier ? 1 : sizeMultiplier)}
                                    height={element.height * (!sizeMultiplier ? 1 : sizeMultiplier)}
                                    x={element.x * (!sizeMultiplier ? 1 : sizeMultiplier)}
                                    y={element.y * (!sizeMultiplier ? 1 : sizeMultiplier)}
                                /> :
                                <StaticSchemeTextElement
                                    key={i}
                                    {...element}
                                    width={element.width * (!sizeMultiplier ? 1 : sizeMultiplier)}
                                    height={element.height * (!sizeMultiplier ? 1 : sizeMultiplier)}
                                    x={element.x * (!sizeMultiplier ? 1 : sizeMultiplier)}
                                    y={element.y * (!sizeMultiplier ? 1 : sizeMultiplier)}
                                />
                            )}
                            </Group>
                        </Layer>
                    </Stage>
                    {popup ? (
                        <SeatPopup
                            {...popup}
                            renderContent={renderSeatPopupContent}
                            onClose={() => setPopup(undefined)}
                        />
                    ) : null}
                </div>
            ) : null}
        </>
    )
}


const formatSeatConditionValue = (condition: BookerBaseSeatPriceCondition) => {
    const formattedValue = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(condition.value)

    if (condition.mode === 'percent') {
        return `${formattedValue}%`
    }

    const currency = currencies.find(c => c.iso === condition.currency)?.sign || condition.currency
    return `${formattedValue}${currency ? ` ${currency}` : ''}`
}

const DefaultSeatPopupContent = ({ seat, priceConditions }: BookerBaseSeatPopupContentProps) => {
    if (!priceConditions.length) {
        return null
    }

    return (
        <Stack spacing={0.75}>
            <Typography variant={'subtitle2'}>Место {seat.name}</Typography>
            {priceConditions.map(condition => {
                const isDiscount = condition.type === 'discount'
                return (
                    <Stack key={condition.id} direction={'row'} spacing={1} justifyContent={'space-between'}>
                        <Typography variant={'body2'} color={isDiscount ? 'success.main' : 'warning.main'}>
                            {isDiscount ? 'Скидка' : 'Доплата'}
                        </Typography>
                        <Typography variant={'body2'} color={isDiscount ? 'success.main' : 'warning.main'}>
                            {isDiscount ? '-' : '+'}{formatSeatConditionValue(condition)}
                        </Typography>
                    </Stack>
                )
            })}
        </Stack>
    )
}

const SeatPopup = ({ position, seat, conditions, renderContent, onClose }: { position: Vector2d, seat: PlaceInfo, conditions: PlaceCondition[], renderContent?: (props: BookerBaseSeatPopupContentProps) => ReactNode, onClose: () => void }) => {
    const theme = useTheme()
    const containerRef = useRef<HTMLDivElement>(null)
    const priceConditions = useMemo<BookerBaseSeatPriceCondition[]>(() => conditions
        .filter((condition): condition is BookerBaseSeatPriceCondition =>
            condition.placeIds.includes(seat.id) && (condition.type === 'discount' || condition.type === 'priceIncrease')),
        [conditions, seat.id])

    const contentProps = useMemo<BookerBaseSeatPopupContentProps>(() => ({
        position,
        seat,
        conditions,
        priceConditions,
        onClose,
    }), [position, seat, conditions, priceConditions, onClose])
    const content = renderContent
        ? renderContent(contentProps)
        : <DefaultSeatPopupContent {...contentProps} />

    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (!isClickedInside(e, containerRef.current)) {
                onClose()
            }
        }

        window.addEventListener('click', onClick)
        return () => {
            window.removeEventListener('click', onClick)
        }
    }, [onClose])

    if (content === null || content === undefined || content === false) {
        return null
    }

    return (
        <div
            ref={containerRef}
            style={{
                position: 'absolute',
                top: position.y + 5 + 'px',
                left: position.x + 5 + 'px',
                padding: '10px',
                borderRadius: '4px',
                boxShadow: theme.shadows[5],
                zIndex: theme.zIndex.tooltip,
                backgroundColor: theme.palette.background.paper,
                textAlign: 'left',
                minWidth: 160,
            }}>
            {content}
        </div>
    )
}


